import _extends from "@babel/runtime/helpers/esm/extends";
import { buildEventsState } from '@mui/x-scheduler-headless/internals';
import { SchedulerDataSourceCacheDefault } from "../utils/cache.mjs";
import { SchedulerDataManager } from "../utils/queue.mjs";
export class SchedulerLazyLoadingPlugin {
  dataManager = null;
  cache = null;
  constructor(store) {
    this.store = store;
    if (this.store.parameters.dataSource) {
      this.cache = new SchedulerDataSourceCacheDefault({
        ttl: 300_000
      });
      this.dataManager = new SchedulerDataManager(this.store.state.adapter, this.loadEventsFromDataSource);

      // Subscribe to events updated event to sync cache
      this.store.subscribeEvent('eventsUpdated', this.handleEventsUpdated);
    }
  }
  queueDataFetchForRange = async (range, immediate = false) => {
    if (this.dataManager) {
      const {
        adapter
      } = this.store.state;

      // Set loading state immediately (before the debounce delay)

      if (this.cache && !this.cache.hasCoverage(adapter.getTime(range.start), adapter.getTime(adapter.endOfDay(range.end)))) {
        this.store.set('isLoading', true);
      }
      if (immediate) {
        await this.dataManager.queueImmediate([range]);
      } else {
        await this.dataManager.queue([range]);
      }
    }
  };

  /**
   * Loads events from the data source.
   */
  loadEventsFromDataSource = async range => {
    const {
      dataSource
    } = this.store.parameters;
    const {
      adapter,
      displayTimezone
    } = this.store.state;
    if (!dataSource || !this.cache || !this.dataManager) {
      return;
    }
    if (this.cache.hasCoverage(adapter.getTime(range.start), adapter.getTime(adapter.endOfDay(range.end)))) {
      const allCachedEvents = this.cache?.getAll() || [];
      const eventsState = buildEventsState(_extends({}, this.store.parameters, {
        events: allCachedEvents
      }), adapter, displayTimezone);
      this.store.update(_extends({}, this.store.state, eventsState, {
        isLoading: false
      }));
      await this.dataManager.setRequestSettled(range);
      return;
    }
    try {
      const events = await dataSource.getEvents(range.start, range.end);
      this.cache.setRange(adapter.getTime(range.start), adapter.getTime(adapter.endOfDay(range.end)), events ?? []);
      const eventsState = buildEventsState(_extends({}, this.store.parameters, {
        events
      }), adapter, displayTimezone);
      this.store.update(_extends({}, this.store.state, eventsState, {
        errors: []
      }));
      // Mark request as settled
      await this.dataManager.setRequestSettled(range);
    } catch (error) {
      this.store.set('errors', [error]);
      await this.dataManager.setRequestSettled(range);
    } finally {
      // Unset loading state
      this.store.set('isLoading', false);
      await this.dataManager.setRequestSettled(range);
    }
  };
  handleEventsUpdated = async params => {
    const {
      deleted,
      updated,
      created,
      newEvents
    } = params;
    const {
      dataSource
    } = this.store.parameters;
    const {
      adapter,
      displayTimezone
    } = this.store.state;
    if (!dataSource || !this.cache) {
      return;
    }
    try {
      const shouldUpdateEvents = await dataSource.updateEvents({
        deleted,
        updated: Array.from(updated.keys()),
        created
      });
      if (!shouldUpdateEvents.success) {
        return;
      }

      // Update cache
      for (const id of deleted) {
        this.cache.remove(String(id));
      }
      const modifiedIds = new Set([...created, ...updated.keys()]);
      if (modifiedIds.size > 0) {
        for (const event of newEvents) {
          // @ts-ignore
          if (modifiedIds.has(event.id)) {
            this.cache.upsert(event);
          }
        }
      }
      const eventsState = buildEventsState(_extends({}, this.store.parameters, {
        events: newEvents
      }), adapter, displayTimezone);
      this.store.update(_extends({}, this.store.state, eventsState));
    } catch (error) {
      this.store.set('errors', [error]);
    }
  };
}